
import React, { useState } from 'react';
import { TrendingUp, TrendingDown, BarChart, ArrowRight } from 'lucide-react';

const SegmentPerformance = ({ segments }) => {
  const [selectedKPI, setSelectedKPI] = useState('performance');
  
  // Sort segments by the selected KPI
  const sortedSegments = [...segments].sort((a, b) => b[selectedKPI] - a[selectedKPI]);
  
  // Chart data for visualizing trends
  const getSparklineData = (segment) => {
    // This would normally come from a data source
    // For demo purposes, we're generating some random data
    return Array(12).fill(0).map((_, i) => {
      const baseValue = segment.performance;
      const variance = Math.random() * 10 - 5;
      return {
        month: i + 1,
        value: Math.max(baseValue + variance, 0)
      };
    });
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Segment Performance</h3>
        <div className="flex">
          <select 
            className="text-xs border border-gray-200 rounded-md px-2 py-1"
            value={selectedKPI}
            onChange={(e) => setSelectedKPI(e.target.value)}
          >
            <option value="performance">Performance</option>
            <option value="stations">Station Count</option>
          </select>
        </div>
      </div>
      
      <div className="space-y-4">
        {sortedSegments.map((segment, index) => {
          // Calculate performance color based on score
          const performanceColor = 
            segment.performance >= 90 ? 'bg-green-100 text-green-800' : 
            segment.performance >= 80 ? 'bg-yellow-100 text-yellow-800' : 
            'bg-red-100 text-red-800';
          
          const sparklineData = getSparklineData(segment);
          
          return (
            <div key={segment.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors cursor-pointer">
              <div className="flex items-center">
                <div className="text-lg font-semibold text-gray-400 mr-3">
                  #{index + 1}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{segment.name}</p>
                  <p className="text-sm text-gray-500">{segment.stations} Stations</p>
                </div>
              </div>
              
              <div className="flex items-center">
                {/* Simple sparkline visualization */}
                <div className="hidden sm:flex mr-4 h-8 w-24 items-end space-x-0.5">
                  {sparklineData.map((item, i) => (
                    <div 
                      key={i}
                      style={{ 
                        height: `${(item.value / 100) * 32}px`,
                        backgroundColor: segment.trend === 'up' ? '#34C759' : '#FF3B30',
                        width: '2px'
                      }}
                    />
                  ))}
                </div>
                
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  segment.performance >= 90 ? 'bg-green-100' : 
                  segment.performance >= 80 ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  <span className={`text-sm font-semibold ${
                    segment.performance >= 90 ? 'text-green-800' : 
                    segment.performance >= 80 ? 'text-yellow-800' : 'text-red-800'
                  }`}>
                    {segment.performance}%
                  </span>
                </div>
                <div className="ml-3">
                  {segment.trend === 'up' ? (
                    <TrendingUp size={18} className="text-green-500" />
                  ) : (
                    <TrendingDown size={18} className="text-red-500" />
                  )}
                </div>
                <ArrowRight size={16} className="ml-2 text-gray-400" />
              </div>
            </div>
          );
        })}
      </div>
      
      <button className="w-full mt-4 text-sm py-2 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-50 flex items-center justify-center">
        <BarChart size={16} className="mr-2" />
        View Detailed Segment Analysis
      </button>
    </div>
  );
};

export default SegmentPerformance;
